import { useState, useEffect, useRef } from 'react';
import CryptoPatternChart from './CryptoPatternChart';
import axios from 'axios';

type SortOption = 'volume' | 'newest' | 'expiry' | 'volatile' | 'crypto';

interface Market {
  id: string;
  question: string;
  outcomePrices: any;
  volume: string;
  volumeNum: number;
  endDate: string;
  startDate: string;
  active: boolean;
  conditionId?: string;
}

interface CryptoMatch {
  date: string;
  similarityScore: number;
  window: number[];
  outcome: number;
}
interface CryptoPatternData {
  coin: string; symbol: string; currentPrice: number;
  currentWindow: number[]; currentDates: string[];
  matches: CryptoMatch[]; bullishCount: number;
  avgOutcome: number; lookbackDays: number; forwardDays: number;
}
interface EvalResult {
  loading: boolean;
  firstPass: boolean | null;
  fullEval?: {
    side: 'YES' | 'NO' | 'SKIP';
    conviction: number;
    reasoning: string;
    yesPrice: number;
    noPrice: number;
  } | null;
  message?: string;
  firstPassReason?: string;
  error?: string;
  pass1Ms?: number;
  pass2Ms?: number;
  skipReason?: string;
  cryptoPattern?: CryptoPatternData;
  simAdded?: boolean;
}

const SORT_OPTIONS: { value: SortOption; label: string; desc: string }[] = [
  { value: 'volume',   label: '📊 Most Volume',       desc: 'Highest trading activity' },
  { value: 'newest',   label: '🆕 Latest Added',       desc: 'Most recently created markets' },
  { value: 'expiry',   label: '⏰ Expiring Soon',      desc: 'Closest to resolution' },
  { value: 'volatile', label: '📈 Most Volatile',      desc: 'Closest to 50/50 split' },
  { value: 'crypto',   label: '🪙 Crypto Only',         desc: 'Bitcoin, Ethereum, Solana, XRP and more' },
];

function fmtDate(d: string) {
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? '—' : dt.toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function fmtVol(v: any) {
  const n = parseFloat(v || '0');
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
}

function parsePrices(raw: any): [number, number] {
  if (Array.isArray(raw)) return [parseFloat(raw[0] || '0'), parseFloat(raw[1] || '0')];
  if (typeof raw === 'string') {
    try {
      const arr = JSON.parse(raw.replace(/'/g, '"'));
      return [parseFloat(arr[0] || '0'), parseFloat(arr[1] || '0')];
    } catch (err) {
      console.error('Failed to parse prices:', err);
    }
  }
  return [0, 0];
}

// Run first-pass screens concurrently, CONCURRENCY at a time
async function runFirstPassBatch(
  markets: Market[],
  onResult: (key: string, result: { pass: boolean; reason: string; ms: number }) => void,
  signal: AbortSignal,
  CONCURRENCY = 8
) {
  let i = 0;
  async function next() {
    while (i < markets.length) {
      if (signal.aborted) return;
      const m = markets[i++];
      const key = m.id || m.conditionId || m.question;
      try {
        const res = await axios.post('/api/first-pass', m, { signal });
        onResult(key, res.data);
      } catch (err: any) {
        if (err.name === 'CanceledError' || err.name === 'AbortError') return;
        onResult(key, { pass: true, reason: 'screen error — passing through', ms: 0 });
      }
    }
  }
  await Promise.all(Array.from({ length: CONCURRENCY }, next));
}

export default function Search() {
  const [sort, setSort] = useState<SortOption>(
    () => (localStorage.getItem('poly_search_sort') as SortOption) || 'volume'
  );
  const [minVolume, setMinVolume] = useState(
    () => parseInt(localStorage.getItem('poly_search_minvol') || '5000')
  );
  const [hideFailures, setHideFailures] = useState(
    () => localStorage.getItem('poly_search_hide_fail') === 'true'
  );
  const [markets, setMarkets] = useState<Market[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [evalResults, setEvalResults] = useState<Record<string, EvalResult>>({});
  const [firstPassRunning, setFirstPassRunning] = useState(false);
  const firstPassAbort = useRef<AbortController | null>(null);
  const [localSort, setLocalSort] = useState<string | null>(
    () => localStorage.getItem('poly_search_local_sort') || null
  );
  const [localSortDir, setLocalSortDir] = useState<'asc' | 'desc'>(
    () => (localStorage.getItem('poly_search_local_sort_dir') as 'asc' | 'desc') || 'asc'
  );

  useEffect(() => { localStorage.setItem('poly_search_minvol', String(minVolume)); }, [minVolume]);
  useEffect(() => { localStorage.setItem('poly_search_hide_fail', String(hideFailures)); }, [hideFailures]);
  useEffect(() => { if (localSort) localStorage.setItem('poly_search_local_sort', localSort); }, [localSort]);
  useEffect(() => { localStorage.setItem('poly_search_local_sort_dir', localSortDir); }, [localSortDir]);

  useEffect(() => {
    localStorage.setItem('poly_search_sort', sort);
  }, [sort]);

  const CRYPTO_KEYWORDS = ['bitcoin', 'ethereum', 'solana', 'xrp', 'crypto', 'bnb', 'doge', 'dogecoin', 'cardano', 'avalanche', 'chainlink', 'polkadot', 'shiba', 'pepe', 'btc', ' eth ', 'litecoin', 'uniswap', 'polygon', 'matic'];

  const handleSearch = async () => {
    // Cancel any running first-pass batch
    firstPassAbort.current?.abort();

    setLoading(true);
    setError('');
    setMarkets([]);
    setTotal(null);
    setEvalResults({});
    setFirstPassRunning(false);
    try {
      let fetched: Market[] = [];
      if (sort === 'crypto') {
        const res = await axios.get('/api/markets/search', { params: { sort: 'volume', limit: 500, minVolume } });
        fetched = (res.data.markets || []).filter((m: Market) =>
          CRYPTO_KEYWORDS.some(kw => (m.question || '').toLowerCase().includes(kw))
        );
        fetched.sort((a, b) => (b.volumeNum || parseFloat(b.volume || '0')) - (a.volumeNum || parseFloat(a.volume || '0')));
        setMarkets(fetched);
        setTotal(fetched.length);
      } else {
        const res = await axios.get('/api/markets/search', { params: { sort, limit: 500, minVolume } });
        fetched = res.data.markets || [];
        if (sort === 'expiry') {
          fetched.sort((a: Market, b: Market) => {
            const aT = a.endDate ? new Date(a.endDate).getTime() : Infinity;
            const bT = b.endDate ? new Date(b.endDate).getTime() : Infinity;
            return aT - bT;
          });
        } else if (sort === 'newest') {
          fetched.sort((a: Market, b: Market) => {
            const aT = a.startDate ? new Date(a.startDate).getTime() : 0;
            const bT = b.startDate ? new Date(b.startDate).getTime() : 0;
            return bT - aT;
          });
        } else {
          fetched.sort((a: Market, b: Market) => (b.volumeNum || parseFloat(b.volume || '0')) - (a.volumeNum || parseFloat(a.volume || '0')));
        }
        setMarkets(fetched);
        setTotal(res.data.total ?? res.data.markets?.length ?? 0);
      }

      // Auto first-pass all results
      if (fetched.length > 0) {
        const abort = new AbortController();
        firstPassAbort.current = abort;
        setFirstPassRunning(true);
        // Pre-populate loading state for all
        setEvalResults(prev => {
          const next = { ...prev };
          for (const m of fetched) {
            const key = m.id || m.conditionId || m.question;
            if (!next[key]) next[key] = { loading: true, firstPass: null };
          }
          return next;
        });
        runFirstPassBatch(
          fetched,
          (key, result) => {
            setEvalResults(prev => ({
              ...prev,
              [key]: { loading: false, firstPass: result.pass, firstPassReason: result.reason, pass1Ms: result.ms },
            }));
          },
          abort.signal
        ).finally(() => {
          if (!abort.signal.aborted) setFirstPassRunning(false);
        });
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEval = async (m: Market) => {
    const key = m.id || m.conditionId || m.question;
    const existing = evalResults[key];
    // If first pass already passed, skip re-running it by sending flag
    setEvalResults(prev => ({ ...prev, [key]: { ...(existing || {}), loading: true, firstPass: existing?.firstPass ?? null } }));
    try {
      const res = await axios.post('/api/eval-market', m);
      setEvalResults(prev => ({ ...prev, [key]: { loading: false, ...res.data } }));
    } catch (err: any) {
      setEvalResults(prev => ({
        ...prev,
        [key]: { loading: false, firstPass: existing?.firstPass ?? null, error: err.response?.data?.error || err.message },
      }));
    }
  };

  const handleSimAdd = async (m: Market, ev: EvalResult) => {
    const key = m.id || m.conditionId || m.question;
    if (!ev.fullEval || ev.fullEval.side === "SKIP") return;
    const [yesPrice, noPrice] = parsePrices(m.outcomePrices);
    try {
      await axios.post("/api/sim-add", {
        marketId: m.conditionId || m.id || "",
        question: m.question,
        side: ev.fullEval.side,
        yesPrice,
        noPrice,
        resolveTime: m.endDate || (m as any).end_date || null,
      });
      setEvalResults(prev => ({ ...prev, [key]: { ...prev[key], simAdded: true } }));
    } catch (err: any) {
      alert(err.response?.data?.error || "Failed to add position");
    }
  };

  const firstPassDone = Object.values(evalResults).filter(e => !e.loading && e.firstPass !== null).length;
  const firstPassTotal = markets.length;
  const visibleMarkets = hideFailures
    ? markets.filter(m => {
        const key = m.id || m.conditionId || m.question;
        const ev = evalResults[key];
        // Show if: not yet screened (still loading/pending) OR passed
        return !ev || ev.loading || ev.firstPass !== false;
      })
    : markets;

  const sortedMarkets = localSort ? [...visibleMarkets].sort((a, b) => {
    let aVal: number, bVal: number;
    if (localSort === 'expiry') {
      aVal = a.endDate ? new Date(a.endDate).getTime() : Infinity;
      bVal = b.endDate ? new Date(b.endDate).getTime() : Infinity;
    } else if (localSort === 'volume') {
      aVal = a.volumeNum || parseFloat(a.volume || '0');
      bVal = b.volumeNum || parseFloat(b.volume || '0');
    } else if (localSort === 'yes') {
      aVal = parsePrices(a.outcomePrices)[0];
      bVal = parsePrices(b.outcomePrices)[0];
    } else if (localSort === 'no') {
      aVal = parsePrices(a.outcomePrices)[1];
      bVal = parsePrices(b.outcomePrices)[1];
    } else { return 0; }
    return localSortDir === 'asc' ? aVal - bVal : bVal - aVal;
  }) : visibleMarkets;

  const handleSortCol = (col: string) => {
    if (localSort === col) {
      setLocalSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setLocalSort(col);
      setLocalSortDir(col === 'expiry' ? 'asc' : 'desc');
    }
  };

  return (
    <div className="search-panel">
      <div className="search-controls">
        <div className="sort-options">
          {SORT_OPTIONS.map(opt => (
            <button
              key={opt.value}
              className={`sort-btn ${sort === opt.value ? 'active' : ''}`}
              onClick={() => setSort(opt.value)}
              title={opt.desc}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <div className="search-action-row">
          <button className="search-btn" onClick={handleSearch} disabled={loading}>
            {loading ? '…' : 'Search'}
          </button>
          <span className="vol-filter-label">Min: {minVolume >= 1000 ? `$${(minVolume/1000).toFixed(0)}K` : `$${minVolume}`}</span>
          <input
            type="range"
            min={0}
            max={50000}
            step={1000}
            value={minVolume}
            onChange={e => setMinVolume(Number(e.target.value))}
            className="vol-slider"
          />
        </div>
      </div>

      {error && <p className="search-error">{error}</p>}

      <div className="results-sticky-header">
      {total !== null && !loading && (
        <div className="search-status-row">
          <p className="search-count">{total} market{total !== 1 ? 's' : ''} returned</p>
          {firstPassRunning && (
            <span className="first-pass-progress">
              ⚡ Screening {firstPassDone}/{firstPassTotal}…
            </span>
          )}
          {!firstPassRunning && firstPassTotal > 0 && firstPassDone > 0 && (
            <label className="hide-failures-label">
              <input
                type="checkbox"
                checked={hideFailures}
                onChange={e => setHideFailures(e.target.checked)}
              />
              Hide first-pass failures
              {hideFailures && visibleMarkets.length !== markets.length && (
                <span className="hidden-count"> ({markets.length - visibleMarkets.length} hidden)</span>
              )}
            </label>
          )}
        </div>
      )}

      {visibleMarkets.length > 0 && (
        <div className="results-sort-bar">
          {([
            { key: 'expiry', label: 'Expiry' },
            { key: 'volume', label: 'Volume' },
            { key: 'yes',    label: 'YES price' },
            { key: 'no',     label: 'NO price' },
          ] as { key: string; label: string }[]).map(col => (
            <button
              key={col.key}
              className={`sort-col-btn ${localSort === col.key ? 'sort-col-active' : ''}`}
              onClick={() => handleSortCol(col.key)}
            >
              {col.label}
              {localSort === col.key ? (localSortDir === 'asc' ? ' ↑' : ' ↓') : ' ↕'}
            </button>
          ))}
        </div>
      )}

      </div>

      {visibleMarkets.length > 0 && (
        <div className="market-list">
          {sortedMarkets.map((m, i) => {
            const [yesPrice, noPrice] = parsePrices(m.outcomePrices);
            const vol = m.volumeNum || parseFloat(m.volume || '0');
            const endDate = m.endDate || (m as any).end_date || (m as any).resolveTime;
            const key = m.id || m.conditionId || m.question;
            const ev = evalResults[key];

            return (
              <div key={m.id || i} className="market-card">
                <div className="market-question">{m.question}</div>
                <div className="market-meta">
                  <span className="market-price yes">YES {(yesPrice * 100).toFixed(0)}¢</span>
                  <span className="market-price no">NO {(noPrice * 100).toFixed(0)}¢</span>
                  <span className="market-vol">{fmtVol(vol)}</span>
                  <span className="market-expiry">Expires {fmtDate(endDate)}</span>
                  {/* Show first-pass screening badge inline while running */}
                  {ev?.loading && !ev.fullEval && (
                    <span className="first-pass-screening">⚡ screening…</span>
                  )}
                  {ev && !ev.loading && ev.firstPass !== null && !ev.fullEval && (
                    <span className={`eval-pass-badge ${ev.firstPass ? 'pass' : 'fail'}`}>
                      {ev.firstPass ? '✓ pass 1' : '✗ fail 1'}{ev.pass1Ms != null ? ` · ${(ev.pass1Ms/1000).toFixed(1)}s` : ''}
                    </span>
                  )}
                  <button
                    className="eval-btn"
                    onClick={() => handleEval(m)}
                    disabled={ev?.loading || (ev?.firstPass === false)}
                    title={ev?.firstPass === false ? 'Failed first pass — not worth full eval' : 'Run full evaluation (web search)'}
                  >
                    {ev?.loading && ev.fullEval === undefined ? '⏳ Evaluating…' : '🔬 Eval'}
                  </button>
                </div>

                {ev && !ev.loading && ev.fullEval !== undefined && (
                  <div className="eval-result">
                    <div className="eval-row">
                      <span className={`eval-pass-badge ${ev.firstPass ? 'pass' : 'fail'}`}>
                        1st pass: {ev.firstPass ? '✓ PASS' : '✗ FAIL'}{ev.pass1Ms != null ? ` · ${(ev.pass1Ms/1000).toFixed(1)}s` : ''}
                      </span>
                      {ev.firstPass && ev.fullEval && (
                        <span className={`eval-side-badge side-${ev.fullEval.side.toLowerCase()}`}>
                          2nd pass: {ev.fullEval.side}{ev.fullEval.conviction > 0 ? ` · conviction ${ev.fullEval.conviction}` : ''}{ev.pass2Ms != null ? ` · ${(ev.pass2Ms/1000).toFixed(1)}s` : ''}
                        </span>
                      )}
                      {ev.firstPass && !ev.fullEval && (
                        <span className="eval-side-badge side-skip">
                          2nd pass: skipped{ev.pass2Ms != null ? ` · ${(ev.pass2Ms/1000).toFixed(1)}s` : ''}
                        </span>
                      )}
                      {ev.firstPass && ev.fullEval && ev.fullEval.side !== 'SKIP' && (
                        ev.simAdded
                          ? <span className="sim-added-badge">✓ Tracking</span>
                          : <button className="sim-add-btn" onClick={() => handleSimAdd(m, ev)} title="Add to positions to track outcome">📌 Track</button>
                      )}
                    </div>
                    {ev.error && <div className="eval-error">{ev.error}</div>}
                    {ev.firstPassReason && (
                      <div className="eval-reasoning">
                        <span className="eval-reasoning-label">1st pass reason:</span>
                        <div className="eval-reasoning-text">{ev.firstPassReason}</div>
                      </div>
                    )}
                    {ev.firstPass && ev.fullEval?.reasoning && (
                      <div className="eval-reasoning">
                        <span className="eval-reasoning-label">🔍 2nd pass reasoning (web search):</span>
                        <div className="eval-reasoning-text">{ev.fullEval.reasoning}</div>
                      </div>
                    )}
                    {ev.firstPass && !ev.fullEval && ev.skipReason && (
                      <div className="eval-reasoning">
                        <span className="eval-reasoning-label">2nd pass skipped because:</span>
                        <div className="eval-reasoning-text">{ev.skipReason}</div>
                      </div>
                    )}
                    {!ev.firstPass && (
                      <div className="eval-reasoning">
                        {ev.message && <div>{ev.message}</div>}
                      </div>
                    )}
                    {ev.cryptoPattern && (
                      <CryptoPatternChart data={ev.cryptoPattern} />
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
